import type {
    PoolPricingMethod,
    PoolPricingSimulationResponse,
} from '@thinkalpha/ui-types/types/admin/lab-pool-pricing';

/** One hierarchy row of a pricing resolution, in configured priority order. */
export interface PricingResolutionRow {
    method: PoolPricingMethod;
    /** Candidate price for the method; `null` when no price was available. */
    candidate: number | null;
    /** `true` for the first method in the hierarchy with a positive candidate. */
    winner: boolean;
}

/** Minimum-price floor as it participated in a resolution. */
export interface PricingResolutionMinimum {
    price: number;
    /** `true` when the floor raised the resolved price above the winning candidate. */
    applied: boolean;
}

/** Presentation model for the pricing sidecard, derived from a simulation response. */
export interface PricingResolution {
    rows: PricingResolutionRow[];
    /** `null` when no floor is configured for the requested borrow status. */
    minimum: PricingResolutionMinimum | null;
    /** Final resolved price; `null` when no method in the hierarchy priced. */
    price: number | null;
}

/** A candidate (or floor) is usable on the wire when it is finite and non-negative. */
export const isPresent = (value: number | undefined): value is number =>
    typeof value === 'number' && Number.isFinite(value) && value >= 0;

/**
 * Product rule (per design): a candidate of `0` renders — an unset admin
 * price arrives as `0` — but is never the winning condition; the first
 * candidate with a positive price wins. NB the backend's `resolvePoolPrice`
 * currently treats `0` as usable; the UI intentionally applies the design
 * semantics until the backend aligns. Shared with `previewDraftSimulation`
 * so the rule lives in one place.
 */
export const isWinning = (value: number | undefined): value is number => isPresent(value) && value > 0;

/**
 * Derives the sidecard's presentation rows from a simulation response.
 *
 * The response names neither the winning method nor whether the floor
 * bound, so this applies the design's selection rule (first hierarchy
 * method with a positive candidate — see {@link isWinning}) and marks the
 * floor applied when the resolved price sits on it above the winner's own
 * candidate. The resolved price and the floor itself are always taken from
 * the response (`minimumPrice` is the floor the backend selected for the
 * requested borrow status), never recomputed.
 */
export function derivePricingResolution(response: PoolPricingSimulationResponse): PricingResolution {
    const { config, candidates, price, minimumPrice } = response;
    const hierarchy = config.hierarchy ?? [];

    const winner: PoolPricingMethod | null = hierarchy.find((method) => isWinning(candidates[method])) ?? null;

    const rows: PricingResolutionRow[] = hierarchy.map((method) => ({
        method,
        candidate: isPresent(candidates[method]) ? candidates[method] : null,
        winner: method === winner,
    }));

    const minimum: PricingResolutionMinimum | null =
        minimumPrice === null
            ? null
            : {
                  price: minimumPrice,
                  applied:
                      price !== null && price === minimumPrice && (winner === null || candidates[winner] !== price),
              };

    return { rows, minimum, price };
}
